import {createContext, PropsWithChildren, useMemo, useRef, useState} from "react";
import {HardwareGroupWithContent, HardwareItem, HwOpening} from "../../../../api/Hardware";
import {useReload} from "./ReloadContext";

export type EditHardwareItem = Omit<HardwareItem, "id">;

export type EditHardwareGroupWithContent = Omit<HardwareGroupWithContent, "hardware"|"openings"|"name"> & {
    name?: string;
    openings: (Partial<HwOpening> & {id: number; name: string;})[];
    hardware: EditHardwareItem[];
    archived?: boolean;
}

export const HwEditingContext = createContext({
    creating: null as null | EditHardwareGroupWithContent,
    editing: null as null | EditHardwareGroupWithContent,

    onCreate: (hw: EditHardwareGroupWithContent): void => {throw new Error("no provider")},
    onEdit: (hw: EditHardwareGroupWithContent): void => {throw new Error("no provider")},
    onDone: (): void => {throw new Error("no provider")}
});

export function HwEditingContextProvider(props: PropsWithChildren<{
    scrollRef: { current: HTMLElement | null};
}>) {
    const [creating, setCreating] = useState<EditHardwareGroupWithContent|null>(null)
    const [editing, setEditing] = useState<EditHardwareGroupWithContent|null>(null);

    useReload(() => {
        setEditing(null);
        setCreating(null)
    }, []);

    const lastIdRef = useRef(0);

    const ctx = useMemo(() => {
        return {
            creating: creating,
            editing: editing,
            onCreate: setCreating,
            onEdit: async (hw: EditHardwareGroupWithContent) => {
                lastIdRef.current = hw.id;
                setEditing(hw);
            },
            onDone: () => {
                setEditing(null);
                setCreating(null);

                // set hash to id of editing item
                if(lastIdRef.current > 0) {
                    window.location.hash = makeHeadingId(lastIdRef.current);
                }
            }
        }
    }, [creating, editing]);

    return (
        <HwEditingContext.Provider value={ctx}>
            {props.children}
        </HwEditingContext.Provider>
    )
}

export function makeHeadingId(id: number) {
    return "hardware-group-" + id
}